<div>
    <h2 class="mat-title">
        College Staff Editor<span *ngIf="AdminCollege"> - {{ AdminCollege.CollegeName }}</span>
    </h2>
    <div>
        <div *ngIf="StaffList">
            There are currently {{ StaffList.length }} college staff (administrators, advisors, and support staff) assigned to this
            college in the DELTA system.
        </div>
        <div>
            Please use the buttons below to add, remove, or change the access level of staff members for this college. Note: If the
            user does not yet have an account in the DELTA system, please contact the Help Desk for more assistance.
        </div>
        <div
            class="buttongroup"
            fxLayout="row"
            fxLayoutAlign="start start"
            fxLayoutGap="20px"
            fxLayout.lt-md="column"
            fxLayoutAlign.lt-md="start center"
            fxLayoutGap.lt-md="10px"
        >
            <button mat-raised-button color="primary" (click)="ShowUserSearch()">Add New College Staff Member</button>
            <button mat-raised-button color="primary" (click)="ShowNewUserForm()">Add New College Staff Member (Needs Account)</button>
            <button mat-raised-button color="accent" (click)="DownloadStaffPDF()">Download Staff Members PDF</button>
        </div>
    </div>

    <!-- New User Form -->
    <div [hidden]="!ShowingNewUserForm" class="mat-elevation-z12 padding">
        <h2 class="mat-headline">New User Panel</h2>
        <div>
            <p>
                This panel can be used to create a new user account for individuals who need access to the DELTA System as college
                staff members. You should <strong>not</strong> use this panel to create a new user account for any other individuals
                such as parents, students, or school staff.
            </p>
            <p>
                Use the form below to provide the required information to generate an account. The username for the account is
                automatically assigned by the district user account system. A randomly generated secure default password will be
                assigned to all new accounts created.
            </p>
            <p>
                <strong>Note: </strong>The user whose account is being created <u>will</u> be emailed the account information along
                with instructions on how to use the district Password Reset Tool to change their default password.
            </p>
        </div>
        <form [formGroup]="NewUserForm" novalidate>
            <div
                fxLayout="row"
                fxLayoutAlign="start center"
                fxLayoutGap="25px"
                fxLayout.lt-md="column"
                fxLayoutAlign.lt-md="start stretch"
            >
                <div
                    fxLayout="row"
                    fxLayoutAlign="start center"
                    fxLayoutGap="15px"
                    fxLayout.lt-md="column"
                    fxLayoutAlign.lt-md="start stretch"
                >
                    <form-label>First Name: </form-label>
                    <mat-form-field>
                        <input type="text" matInput formControlName="FirstName" placeholder="First Name..." maxlength="50" />
                        <mat-error *ngIf="NewUserForm.controls['FirstName'].hasError('required')">User first name required</mat-error>
                    </mat-form-field>
                </div>
                <div
                    fxLayout="row"
                    fxLayoutAlign="start center"
                    fxLayoutGap="15px"
                    fxLayout.lt-md="column"
                    fxLayoutAlign.lt-md="start stretch"
                >
                    <form-label>Last Name: </form-label>
                    <mat-form-field>
                        <input type="text" matInput formControlName="LastName" placeholder="Last Name..." maxlength="50" />
                        <mat-error *ngIf="NewUserForm.controls['LastName'].hasError('required')">User last name required</mat-error>
                    </mat-form-field>
                </div>
                <div
                    fxLayout="row"
                    fxLayoutAlign="start center"
                    fxLayoutGap="15px"
                    fxLayout.lt-md="column"
                    fxLayoutAlign.lt-md="start stretch"
                >
                    <form-label>Email: </form-label>
                    <mat-form-field>
                        <input type="text" matInput formControlName="Email" placeholder="Email Address..." maxlength="100" />
                        <mat-error *ngIf="NewUserForm.controls['Email'].hasError('required')">User email address required</mat-error>
                        <mat-error *ngIf="NewUserForm.controls['Email'].hasError('validateEmail')"
                            >Invalid email address format</mat-error
                        >
                    </mat-form-field>
                </div>
                <div
                    fxLayout="row"
                    fxLayoutAlign="start center"
                    fxLayoutGap="15px"
                    fxLayout.lt-md="column"
                    fxLayoutAlign.lt-md="start stretch"
                >
                    <form-label>Email Verification: </form-label>
                    <mat-form-field>
                        <input
                            type="text"
                            matInput
                            formControlName="EmailVerify"
                            placeholder="Email Address Verification..."
                            maxlength="100"
                            [errorStateMatcher]="parentErrorStateMatcher"
                        />
                        <mat-error *ngIf="NewUserForm.controls['EmailVerify'].hasError('required')"
                            >User email address required</mat-error
                        >
                        <mat-error *ngIf="NewUserForm.controls['EmailVerify'].hasError('validateEmail')"
                            >Invalid email address format</mat-error
                        >
                        <mat-error *ngIf="NewUserForm.hasError('EmailVerifyValidate') && !NewUserForm.controls['EmailVerify'].errors"
                            >Verification email does not match original email</mat-error
                        >
                    </mat-form-field>
                </div>
            </div>
        </form>
        <div
            class="actionbar"
            fxLayout="row"
            fxLayoutAlign="end center"
            fxLayoutGap="15px"
            fxLayout.lt-md="column"
            fxLayoutAlign.lt-md="start center"
        >
            <button type="text" mat-raised-button color="primary" (click)="AddNewUser(0)" [disabled]="NewUserForm.invalid">
                Add User As Support Staff
            </button>
            <button type="text" mat-raised-button color="primary" (click)="AddNewUser(1)" [disabled]="NewUserForm.invalid">
                Add User As Advisor
            </button>
            <button type="text" mat-raised-button color="primary" (click)="AddNewUser(2)" [disabled]="NewUserForm.invalid">
                Add User As Administrator
            </button>
            <button type="text" mat-raised-button color="warn" (click)="ResetNewUserForm()">Cancel</button>
        </div>
    </div>

    <!-- Staff Table -->
    <mat-table class="bordered" #table [dataSource]="StaffData" matSort matSortActive="LastName" matSortDirection="asc">
        <ng-container matColumnDef="LastName">
            <mat-header-cell *matHeaderCellDef mat-sort-header class="centered"><span>Last Name</span></mat-header-cell>
            <mat-cell *matCellDef="let staff" class="centered"
                ><span>{{ staff.LastName | titleCase }}</span></mat-cell
            >
        </ng-container>

        <ng-container matColumnDef="FirstName">
            <mat-header-cell *matHeaderCellDef mat-sort-header class="centered">First Name</mat-header-cell>
            <mat-cell *matCellDef="let staff" class="centered">{{ staff.FirstName | titleCase }}</mat-cell>
        </ng-container>

        <ng-container matColumnDef="AccessLevel">
            <mat-header-cell *matHeaderCellDef mat-sort-header class="centered">Access Level</mat-header-cell>
            <mat-cell *matCellDef="let staff" class="centered">
                <span *ngIf="staff.AccessLevel == 2">Administrator</span>
                <span *ngIf="staff.AccessLevel == 1">Advisor</span>
                <span *ngIf="staff.AccessLevel == 0">Support Staff</span>
            </mat-cell>
        </ng-container>

        <ng-container matColumnDef="StaffActions">
            <mat-header-cell *matHeaderCellDef class="centered">Staff Action(s)</mat-header-cell>
            <mat-cell *matCellDef="let staff" class="centered">
                <div fxLayout="column" fxLayoutAlign="start center" fxLayoutGap="10px" class="margin-top margin-bottom">
                    <button mat-raised-button color="primary" (click)="ChangeAccess(staff)">
                        <span>Change Access Level</span>
                    </button>
                    <button mat-raised-button color="warn" (click)="RemoveAccess(staff)">
                        <span>Remove All Access</span>
                    </button>
                </div>
            </mat-cell>
        </ng-container>

        <ng-container matColumnDef="SmallDisplay">
            <mat-header-cell *matHeaderCellDef class="centered">Staff Member(s)</mat-header-cell>
            <mat-cell *matCellDef="let staff">
                <div fxLayout="column" fxLayoutAlign="start stretch">
                    <div fxLayout="row" fxLayoutAlign="space-between center">
                        <div>Last Name</div>
                        <div>{{ staff.LastName | titleCase }}</div>
                    </div>
                    <div fxLayout="row" fxLayoutAlign="space-between center">
                        <div>First Name</div>
                        <div>{{ staff.FirstName | titleCase }}</div>
                    </div>
                    <div fxLayout="row" fxLayoutAlign="space-between center">
                        <div>Access Level</div>
                        <span *ngIf="staff.AccessLevel == 2">Administrator</span>
                        <span *ngIf="staff.AccessLevel == 1">Advisor</span>
                        <span *ngIf="staff.AccessLevel == 0">Support Staff</span>
                    </div>
                    <div class="smalldisplaybuttongroup" fxLayout="column" fxLayoutAlign="start center" fxLayoutGap="10px">
                        <button mat-raised-button color="primary" (click)="ChangeAccess(staff)">
                            <span>Change Access Level</span>
                        </button>
                        <button mat-raised-button color="warn" (click)="RemoveAccess(staff)">
                            <span>Remove All Access</span>
                        </button>
                    </div>
                </div>
            </mat-cell>
        </ng-container>

        <ng-container matColumnDef="NoData">
            <mat-header-cell *matHeaderCellDef>&nbsp;</mat-header-cell>
            <mat-cell *matCellDef="let staff"><em>No college staff members have been set for this college.</em></mat-cell>
        </ng-container>

        <mat-header-row *matHeaderRowDef="VisibleHeaders"></mat-header-row>
        <mat-row *matRowDef="let row; columns: VisibleColumns; let $even = even" [class.primarybackground50]="$even"></mat-row>
    </mat-table>
    <mat-paginator
        #paginator
        [length]="StaffData.data.length"
        [pageIndex]="0"
        [pageSize]="SmallDisplay ? 5 : 10"
        [pageSizeOptions]="[5, 10, 25, 100]"
    ></mat-paginator>
</div>



// WEBPACK FOOTER //
// ./src/app/managestaff/managestaff.component.html